import { format, formatDistanceToNow, Locale, parseISO } from 'date-fns'
import { enUS, sr } from 'date-fns/locale'
import { SETTINGS_KEYS } from '../config/environment'
import { settingsService } from '../services/settings.service'

const srLatinMonths = ['januar', 'februar', 'mart', 'april', 'maj', 'jun', 'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar']

const srLatinMonthsShort = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'avg', 'sep', 'okt', 'nov', 'dec']

const srLatinDays = ['nedelja', 'ponedeljak', 'utorak', 'sreda', 'četvrtak', 'petak', 'subota']

const cyrToLatShort: Record<string, string> = {
  јан: 'jan',
  феб: 'feb',
  мар: 'mar',
  апр: 'apr',
  мај: 'maj',
  јун: 'jun',
  јул: 'jul',
  авг: 'avg',
  сеп: 'sep',
  окт: 'okt',
  нов: 'nov',
  дец: 'dec',
}

const cyrToLatFull: Record<string, string> = {
  јануар: 'januar',
  фебруар: 'februar',
  март: 'mart',
  април: 'april',
  мај: 'maj',
  јун: 'jun',
  јул: 'jul',
  август: 'avgust',
  септембар: 'septembar',
  октобар: 'oktobar',
  новембар: 'novembar',
  децембар: 'decembar',
}

const transliterateToLatin = (text: string): string => {
  return text
    .replace(/јан|феб|мар|апр|мај|јун|јул|авг|сеп|окт|нов|дец/gi, (match) => {
      return cyrToLatShort[match.toLowerCase()] || match
    })
    .replace(/јануар|фебруар|март|април|мај|јун|јул|август|септембар|октобар|новембар|децембар/gi, (match) => {
      return cyrToLatFull[match.toLowerCase()] || match
    })
}

const srLatin: Locale = {
  ...sr,
  localize: {
    ...sr.localize,
    month: (n: number, options?: any) => {
      if (options?.width === 'abbreviated' || options?.width === 'narrow' || options?.width === 'short') {
        return srLatinMonthsShort[n]
      }
      return srLatinMonths[n]
    },
    day: (n: number) => srLatinDays[n],
  },
  options: {
    ...sr.options,
  },
}

const LOCALE_MAP: Record<string, Locale> = {
  'sr-cs': sr,
  sr: sr,
  'sr-latn': srLatin,
  'en-us': enUS,
  en: enUS,
}

const getLocale = (localeCode: string | null | undefined): Locale | undefined => {
  if (!localeCode || localeCode === '' || localeCode === 'iso-8601') {
    return undefined
  }
  const normalized = localeCode.toLowerCase()
  return LOCALE_MAP[normalized] || LOCALE_MAP['sr-cs']
}

const getDateFormat = (formatStr: string, localeCode?: string | null): string => {
  const isISO = localeCode === 'iso-8601'
  const locale = getLocale(localeCode)
  const isSerbian = locale === sr || locale === srLatin || !locale

  switch (formatStr) {
    case 'shortDate':
      if (isISO) return 'yyyy-MM-dd'
      return isSerbian ? 'dd.MM.yyyy' : 'MM/dd/yyyy'
    case 'mediumDate':
      if (isISO) return 'yyyy-MM-dd'
      return isSerbian ? 'dd MMM yyyy' : 'MMM dd, yyyy'
    case 'longDate':
      if (isISO) return 'yyyy-MM-dd'
      if (localeCode === 'sr-latn') {
        return 'dd. MMMM yyyy'
      }
      return isSerbian ? 'dd. MMMM yyyy' : 'MMMM dd, yyyy'
    case 'fullDate':
      if (isISO) return 'yyyy-MM-dd'
      return isSerbian ? 'EEEE, dd. MMMM yyyy' : 'EEEE, MMMM dd, yyyy'
    default:
      if (isISO) return 'yyyy-MM-dd'
      return isSerbian ? 'dd.MM.yyyy' : 'MM/dd/yyyy'
  }
}

export const formatDate = (dateString: string, formatStr?: string, localeOverride?: string | null): string => {
  try {
    const date = parseISO(dateString)

    const userFormat = formatStr || settingsService.get(SETTINGS_KEYS.DATE_FORMAT) || 'shortDate'
    const userLocale = localeOverride !== undefined ? localeOverride : settingsService.get(SETTINGS_KEYS.DATE_LOCALE) || 'sr-cs'

    if (userFormat === 'relative') {
      const locale = getLocale(userLocale)
      return formatDistanceToNow(date, { addSuffix: true, locale })
    }

    const dateFnsFormat = getDateFormat(userFormat, userLocale)
    const locale = getLocale(userLocale)

    let formatted = format(date, dateFnsFormat, { locale })

    if (userLocale === 'sr-latn') {
      formatted = transliterateToLatin(formatted)
    }

    return formatted
  } catch (error) {
    return dateString
  }
}

const getDateTimeFormat = (formatStr: string, localeCode?: string | null): string => {
  const datePart = getDateFormat(formatStr, localeCode)
  const locale = getLocale(localeCode)
  const isSerbian = locale === sr || locale === srLatin || !locale
  const timePart = isSerbian ? ' HH:mm' : ' hh:mm a'
  return datePart + timePart
}

export const formatDateTime = (dateString: string): string => {
  try {
    const date = parseISO(dateString)
    const userFormat = settingsService.get(SETTINGS_KEYS.DATE_FORMAT) || 'shortDate'
    const userLocale = settingsService.get(SETTINGS_KEYS.DATE_LOCALE) || 'sr-cs'
    const locale = getLocale(userLocale)
    const dateTimeFormat = getDateTimeFormat(userFormat, userLocale)
    let formatted = format(date, dateTimeFormat, { locale })
    if (userLocale === 'sr-latn') {
      formatted = transliterateToLatin(formatted)
    }
    return formatted
  } catch (error) {
    return dateString
  }
}

export const formatRelative = (dateString: string): string => {
  try {
    const date = parseISO(dateString)
    const userLocale = settingsService.get(SETTINGS_KEYS.DATE_LOCALE) || 'sr-cs'
    const locale = getLocale(userLocale)
    return formatDistanceToNow(date, { addSuffix: true, locale })
  } catch (error) {
    return dateString
  }
}
